// package imports
import { useRef } from 'react';
import { useButton, useMenuTrigger } from 'react-aria';
import { useMenuTriggerState } from 'react-stately';
import { Session } from 'next-auth';
import Image from 'next/image';
import Link from 'next/link';
import { KitButton } from '@boystownorg/bi-cms-component-lib';

// // local imports
import { SiteHeaderType } from './SiteHeaderTypes';
import SvgHamburger from './Icons/hamburger.svg';
import MainMenu from './MobileMegaMenuComponents/MainMenu';
import { MobileSiteHeaderProvider } from './MobileMegaMenuComponents/MobileMenuContext';
import { SharedHeaderType } from './SharedHeaderTypes';
import { filterNavigationLinks } from './SiteHeaderUtils';

const MobileMegaMenu = ({
  headerFields,
  sharedHeaderFields,
  session,
}: {
  headerFields: SiteHeaderType;
  sharedHeaderFields: SharedHeaderType;
  session: Session | null;
}) => {
  let ref = useRef<HTMLButtonElement | null>(null);
  const state = useMenuTriggerState({});

  const ctaButtonList = filterNavigationLinks(
    headerFields?.fields?.mobileCTAButtonLink,
    session,
    true,
  );

  const topButtonData = ctaButtonList[0]
    ? {
        link: ctaButtonList[0].fields.link.href,
        text: ctaButtonList[0].fields.link.text,
      }
    : {
        link: '/plan-options',
        text: 'Pricing',
      };

  const { menuTriggerProps, menuProps } = useMenuTrigger(
    {
      type: 'menu',
    },
    state,
    ref,
  );

  const { buttonProps } = useButton(menuTriggerProps, ref);

  return (
    <MobileSiteHeaderProvider state={state}>
      <div className="grid grid-cols-[1fr,auto,1fr] lg:hidden">
        <div className="flex justify-start">
          <KitButton
            href={topButtonData.link}
            color="success"
            style={{
              margin: 0,
              height: '100%',
              paddingRight: '1.5rem',
              borderRadius: 0,
              borderTopRightRadius: '9999px',
              borderBottomRightRadius: '9999px',
            }}
            className="flex-none rounded-none rounded-r-full tracking-wider pl-3 pr-4 -mb-px"
          >
            {topButtonData.text}
          </KitButton>
        </div>

        <div className="flex flex-auto py-3 px-4 items-center justify-center">
          <Link href="/">
            <Image
              src={headerFields?.fields?.megaMenuLogo?.url}
              priority
              width={144}
              height={0}
              quality={100}
              unoptimized
              className="h-8 object-contain"
              alt={headerFields?.fields?.siteName}
            />
          </Link>
        </div>

        <div className="flex justify-end">
          <button
            ref={ref}
            aria-label="Open menu"
            {...buttonProps}
            type="button"
            className="flex items-center justify-center w-14 h-14 border-l border-gray-300 outline-none"
          >
            <SvgHamburger className="stroke-current w-6 h-6" />
          </button>
        </div>
      </div>

      <MainMenu
        menuProps={menuProps}
        data={headerFields}
        session={session}
        sharedHeaderFields={sharedHeaderFields}
      />
    </MobileSiteHeaderProvider>
  );
};

export default MobileMegaMenu;
